import { useMemo } from "react";
import { Linking, Pressable, StyleSheet, Text, View } from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { WEB_URL } from "../../env";
import { type Colors, useColors } from "../../theme";
import { Section } from "./section";

// About surface (design_handoff_2 §9): Privacy policy / Terms of service /
// Help & support link rows. Each opens the canonical public page on the
// marketing site in the system browser — these are the same URLs we hand to
// the App Store / Play submissions.
const LINKS: ReadonlyArray<{ label: string; path: string }> = [
  { label: "Privacy policy", path: "/privacy" },
  { label: "Terms of service", path: "/terms" },
  { label: "Help & support", path: "/support" },
];

export function AboutSection(): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  return (
    <Section title="About">
      <View style={styles.list}>
        {LINKS.map(({ label, path }, i) => (
          <Pressable
            key={path}
            onPress={() => {
              void Linking.openURL(`${WEB_URL}${path}`);
            }}
            accessibilityRole="link"
            accessibilityLabel={label}
            testID={`settings-about-${path.slice(1)}`}
            style={[styles.row, i < LINKS.length - 1 && styles.rowDivider]}
          >
            <Text style={styles.rowLabel}>{label}</Text>
            <Text style={styles.chevron}>›</Text>
          </Pressable>
        ))}
      </View>
    </Section>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    list: {
      borderWidth: 1,
      borderColor: colors.borderSoft,
      borderRadius: radius.md,
      overflow: "hidden",
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: space[4],
      paddingHorizontal: space[4],
    },
    rowDivider: {
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSoft,
    },
    rowLabel: {
      ...textStyle.body,
      fontFamily: fontFamily.regular,
      color: colors.textPrimary,
    },
    chevron: {
      ...textStyle.body,
      color: colors.textMuted,
    },
  });
}
